import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { apiPath } from "@/lib/apiPath";
import type { CodeChannel, CodeStatus } from "../../../lib/codeChannels";

interface SignupPayload {
    phone: string;
    company_id: number;
    name?: string;
    email?: string;
    password?: string;
}

interface LoginPayload {
    phone: string;
    password: string;
    company_id: number;
}

interface AuthResponse {
    access_token: string;
    expires_in: number;
    client_id: number;
    phone: string;
    is_verified: boolean;
    name?: string;
}

/** Sign-up creates nothing: it answers with the number the code goes to. */
interface SignupResponse {
    otp_required: true;
    phone: string;
}

interface OtpRequestPayload {
    phone: string;
    company_id: number;
    /** Telegram first, SMS on request; a backend from before the choice ignores it. */
    channel?: CodeChannel;
    /** The widget's language, as the client picked it: the SMS code's words go in it. */
    locale?: string;
}

interface OtpRequestResponse {
    message: string;
    expires_in: number;
    channel?: CodeChannel;
}

interface OtpStatusPayload {
    phone: string;
    company_id: number;
}

interface OtpStatusResponse {
    status: CodeStatus;
    channel: CodeChannel | null;
    /** The app the code went to: telegram · sms · whatsapp · viber (absent on an older backend). */
    via?: string | null;
}

interface OtpVerifyPayload {
    phone: string;
    code: string;
    company_id: number;
    name?: string;
    email?: string;
    password?: string;
}

/** The confirmed code creates / signs into the account and returns the session. */
interface OtpVerifyResponse extends AuthResponse {
    verified: boolean;
}

export function useClientSignup() {
    return useMutation({
        mutationKey: ["client", "signup"],
        mutationFn: async (payload: SignupPayload): Promise<SignupResponse> => {
            const { data } = await apiClient.post(apiPath("/public/client/signup"), payload);
            return data;
        },
    });
}

export function useClientLogin() {
    return useMutation({
        mutationKey: ["client", "login"],
        mutationFn: async (payload: LoginPayload): Promise<AuthResponse> => {
            const { data } = await apiClient.post(apiPath("/public/client/login"), payload);
            return data;
        },
    });
}

export function useRequestOtp() {
    return useMutation({
        mutationKey: ["client", "otp", "request"],
        mutationFn: async (payload: OtpRequestPayload): Promise<OtpRequestResponse> => {
            const { data } = await apiClient.post(apiPath("/public/client/otp/request"), payload);
            return data;
        },
    });
}

/** How the latest code is doing — a Telegram code is sent a few seconds after the request. */
export function useOtpStatus() {
    return useMutation({
        mutationKey: ["client", "otp", "status"],
        mutationFn: async (payload: OtpStatusPayload): Promise<OtpStatusResponse> => {
            const { data } = await apiClient.post(apiPath("/public/client/otp/status"), payload);
            return data;
        },
    });
}

export function useVerifyOtp() {
    return useMutation({
        mutationKey: ["client", "otp", "verify"],
        mutationFn: async (payload: OtpVerifyPayload): Promise<OtpVerifyResponse> => {
            const { data } = await apiClient.post(apiPath("/public/client/otp/verify"), payload);
            return data;
        },
    });
}
